import type { ReactNode } from 'react';
import { createContext, useContext, useMemo, useRef } from 'react';
import { useSwipeDownToClose } from './hooks/useSwipeDownToClose';

interface Context
  extends Pick<
    ReturnType<typeof useSwipeDownToClose>,
    'swipeRef' | 'onMouseDown' | 'onTouchStart'
  > {
  onClose: () => void;
}

export const SwipeContext = createContext<Context | undefined>(undefined);

interface Props {
  onClose: () => void;
  children: ReactNode;
}

export const SwipeProvider = ({ children, onClose }: Props) => {
  const onCloseRef = useRef(onClose);
  const { swipeRef, onMouseDown, onTouchStart } = useSwipeDownToClose(onClose);
  const value = useMemo(
    () => ({
      swipeRef,
      onMouseDown,
      onTouchStart,
      onClose: onCloseRef.current,
    }),
    [swipeRef, onMouseDown, onTouchStart]
  );
  return (
    <SwipeContext.Provider value={value}>{children}</SwipeContext.Provider>
  );
};

export const useSwipeContext = () => {
  const context = useContext(SwipeContext);
  if (typeof context === 'undefined') {
    throw new Error(
      'useSwipeContext: Attempting to access undefined context, possibly outside of `SwipeProvider`'
    );
  }
  return context;
};
